import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { DNSRecord } from "./DNSRecord";
import { OrganizationEmail } from './OrganizationEmail';
import { PayconiqAccount, PrivatePaymentConfiguration } from './PaymentConfiguration';
import { PaymentMethod } from './PaymentMethod';
import { PaymentProvider } from './PaymentProvider';
import { PermissionRoleDetailed } from './Permissions';
export class CreditItem extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Credits in cents
         */
        this.change = 0;
        /**
         * Credits in cents
         */
        this.date = new Date();
    }
    /**
     * tmp fix
     */
    static patchIdentifier() {
        return StringDecoder;
    }
}
__decorate([
    field({ decoder: IntegerDecoder })
], CreditItem.prototype, "change", void 0);
__decorate([
    field({ decoder: DateDecoder })
], CreditItem.prototype, "date", void 0);
__decorate([
    field({ decoder: StringDecoder })
], CreditItem.prototype, "description", void 0);
export var MollieStatus;
(function (MollieStatus) {
    MollieStatus["NeedsData"] = "NeedsData";
    MollieStatus["InReview"] = "InReview";
    MollieStatus["Completed"] = "Completed";
})(MollieStatus || (MollieStatus = {}));
export var AcquisitionType;
(function (AcquisitionType) {
    AcquisitionType["Recommended"] = "Recommended";
    AcquisitionType["Seen"] = "Seen";
    AcquisitionType["SocialMedia"] = "SocialMedia";
    AcquisitionType["Search"] = "Search";
    AcquisitionType["Other"] = "Other";
})(AcquisitionType || (AcquisitionType = {}));
export var MollieProfileMode;
(function (MollieProfileMode) {
    MollieProfileMode["Test"] = "test";
    MollieProfileMode["Live"] = "live";
})(MollieProfileMode || (MollieProfileMode = {}));
export var MollieProfileStatus;
(function (MollieProfileStatus) {
    MollieProfileStatus["Unverified"] = "unverified";
    MollieProfileStatus["Verified"] = "verified";
    MollieProfileStatus["Blocked"] = "blocked";
})(MollieProfileStatus || (MollieProfileStatus = {}));
export class MollieProfile extends AutoEncoder {
}
__decorate([
    field({ decoder: StringDecoder })
], MollieProfile.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(MollieProfileMode) })
], MollieProfile.prototype, "mode", void 0);
__decorate([
    field({ decoder: StringDecoder })
], MollieProfile.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], MollieProfile.prototype, "website", void 0);
__decorate([
    field({ decoder: new EnumDecoder(MollieProfileStatus) })
], MollieProfile.prototype, "status", void 0);
export class MollieOnboarding extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.canReceivePayments = false;
        this.canReceiveSettlements = false;
    }
}
__decorate([
    field({ decoder: BooleanDecoder })
], MollieOnboarding.prototype, "canReceivePayments", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], MollieOnboarding.prototype, "canReceiveSettlements", void 0);
__decorate([
    field({ decoder: new EnumDecoder(MollieStatus) })
], MollieOnboarding.prototype, "status", void 0);
export class BuckarooSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.key = "";
        this.secret = "";
        this.paymentMethods = [PaymentMethod.Bancontact, PaymentMethod.iDEAL, PaymentMethod.Payconiq];
    }
}
__decorate([
    field({ decoder: StringDecoder })
], BuckarooSettings.prototype, "key", void 0);
__decorate([
    field({ decoder: StringDecoder })
], BuckarooSettings.prototype, "secret", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(PaymentMethod)) })
], BuckarooSettings.prototype, "paymentMethods", void 0);
export class ExternalSyncData extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.lastExternalSync = null;
        this.lastSyncedBy = null;
        this.lastDeleted = null;
        this.counts = new Map();
    }
}
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], ExternalSyncData.prototype, "lastExternalSync", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], ExternalSyncData.prototype, "lastSyncedBy", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], ExternalSyncData.prototype, "lastDeleted", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, IntegerDecoder) })
], ExternalSyncData.prototype, "counts", void 0);
export class OrganizationPrivateMetaData extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * @deprecated
         */
        this.privateKey = null;
        /**
         * DNS records that need to be set in order to activate mail domain and registration domain
         */
        this.dnsRecords = [];
        /**
         * Register domain that is awaiting validation
         */
        this.pendingRegisterDomain = null;
        this.externalSyncData = null;
        this.externalGroupNumber = null;
        /**
         * Mail domain that is awaiting validation
         */
        this.pendingMailDomain = null;
        /**
         * The mail from domain that should be used (if validated).
         * This domain should have the SPF record and MX records needed to handle bounces
         */
        this.mailFromDomain = null;
        /**
         * Mail domain that is used to send e-mails. You can't set this directly, the server will set this value as soon as the domain has been validated.
         */
        this.mailDomain = null;
        this.mailDomainActive = false;
        /**
         * E-mail addresses that an organization can send from (or reply-to)
         */
        this.emails = [];
        /**
         * @deprecated
         * Credits in cents
         */
        this.credits = [];
        // readonly
        this.mollieOnboarding = null;
        this.mollieProfile = null;
        /**
         * When set, Buckaroo has priority over Mollie as a payment provider
         */
        this.buckarooSettings = null;
        this.registrationPaymentConfiguration = PrivatePaymentConfiguration.create({});
        this.featureFlags = [];
        /**
         * Null = use environment default
         */
        this.useTestPayments = null;
        // Only set for admins
        this.payconiqAccounts = [];
        this.acquisitionTypes = [];
        this.roles = [];
        this.billingContact = null;
        /**
         * @deprecated
         * Moved to public meta data. Do not use.
         */
        this.VATNumber = null;
    }
    /**
     * @deprecated
     */
    get registrationProviderConfiguration() {
        return this.registrationPaymentConfiguration;
    }
    get payconiqApiKey() {
        if (this.payconiqAccounts.length === 0) {
            return null;
        }
        return this.payconiqAccounts[0].apiKey;
    }
    get actualTestPayments() {
        if (this.useTestPayments !== null) {
            return this.useTestPayments;
        }
        return STAMHOOFD.environment !== 'production';
    }
    getPaymentProviderFor(method, stripeAccountMeta) {
        var _a;
        if (method === PaymentMethod.Unknown || method === PaymentMethod.Transfer || method === PaymentMethod.PointOfSale) {
            return null;
        }
        if (this.payconiqApiKey && method === PaymentMethod.Payconiq) {
            return PaymentProvider.Payconiq;
        }
        if (stripeAccountMeta) {
            if (stripeAccountMeta.paymentMethods.includes(method)) {
                return PaymentProvider.Stripe;
            }
        }
        // Is Buckaroo setup?
        if (this.buckarooSettings !== null) {
            if (this.buckarooSettings.paymentMethods.includes(method)) {
                return PaymentProvider.Buckaroo;
            }
        }
        if ((this.mollieOnboarding && (((_a = this.mollieOnboarding) === null || _a === void 0 ? void 0 : _a.canReceivePayments) || this.actualTestPayments)) && (method == PaymentMethod.Bancontact || method == PaymentMethod.iDEAL || method == PaymentMethod.CreditCard)) {
            return PaymentProvider.Mollie;
        }
        return null;
    }
}
__decorate([
    field({ decoder: StringDecoder, version: 149, nullable: true })
], OrganizationPrivateMetaData.prototype, "privateKey", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DNSRecord), version: 6 })
], OrganizationPrivateMetaData.prototype, "dnsRecords", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 84 })
], OrganizationPrivateMetaData.prototype, "pendingRegisterDomain", void 0);
__decorate([
    field({ decoder: ExternalSyncData, nullable: true, optional: true })
], OrganizationPrivateMetaData.prototype, "externalSyncData", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], OrganizationPrivateMetaData.prototype, "externalGroupNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 6 })
], OrganizationPrivateMetaData.prototype, "pendingMailDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 84 })
], OrganizationPrivateMetaData.prototype, "mailFromDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 6 })
], OrganizationPrivateMetaData.prototype, "mailDomain", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 8 })
], OrganizationPrivateMetaData.prototype, "mailDomainActive", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationEmail), version: 9 })
], OrganizationPrivateMetaData.prototype, "emails", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(CreditItem), version: 23, optional: true })
], OrganizationPrivateMetaData.prototype, "credits", void 0);
__decorate([
    field({ decoder: MollieOnboarding, nullable: true, version: 27 })
], OrganizationPrivateMetaData.prototype, "mollieOnboarding", void 0);
__decorate([
    field({ decoder: MollieProfile, nullable: true, version: 200 })
], OrganizationPrivateMetaData.prototype, "mollieProfile", void 0);
__decorate([
    field({ decoder: BuckarooSettings, nullable: true, version: 152 })
], OrganizationPrivateMetaData.prototype, "buckarooSettings", void 0);
__decorate([
    field({ decoder: PrivatePaymentConfiguration, version: 176, field: 'registrationProviderConfiguration' }),
    field({ decoder: PrivatePaymentConfiguration, version: 204 })
], OrganizationPrivateMetaData.prototype, "registrationPaymentConfiguration", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 161 })
], OrganizationPrivateMetaData.prototype, "featureFlags", void 0);
__decorate([
    field({ decoder: BooleanDecoder, nullable: true, version: 154 })
], OrganizationPrivateMetaData.prototype, "useTestPayments", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 29, field: 'payconiqApiKey' }),
    field({
        decoder: new ArrayDecoder(PayconiqAccount),
        version: 206,
        upgrade: (oldValue) => {
            if (oldValue === null) {
                return [];
            }
            return [PayconiqAccount.create({ apiKey: oldValue })];
        },
        downgrade: (newValue) => {
            if (newValue.length === 0) {
                return null;
            }
            return newValue[0].apiKey;
        }
    })
], OrganizationPrivateMetaData.prototype, "payconiqAccounts", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(AcquisitionType)), version: 56 })
], OrganizationPrivateMetaData.prototype, "acquisitionTypes", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRoleDetailed), version: 60 })
], OrganizationPrivateMetaData.prototype, "roles", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 88 })
], OrganizationPrivateMetaData.prototype, "billingContact", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 86, optional: true })
], OrganizationPrivateMetaData.prototype, "VATNumber", void 0);
//# sourceMappingURL=OrganizationPrivateMetaData.js.map